import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { useGalleryChildDropTarget } from '@/hooks/dragDrop/useGalleryChildDropTarget';
import { ChildNodeMenuItems } from '@/components/browser/child-node-menu-items';
import { type ChildTreeNode, toSelectedNode } from '@/lib/child-node';
import { NodeChip } from '@/components/node-chip';

/**
 * Tile view's child folder/album: the shared chip, plus an ASSET drop target and the full node
 * context menu. Not draggable — children are deliberately drop-only on this surface (DM-2517).
 *
 * The drop ref sits on the wrapper rather than on the chip itself, so the chip is free to be the
 * context-menu trigger. Radix's Slot does compose its ref with the child's, so a single element
 * would also work; keeping them separate means neither concern depends on that behaviour.
 * `__tests__/component/gallery-child-drop-target.test.tsx` pins the droppable actually registering
 * through a real DndContext, in both this shape and the row's single-element shape.
 */
export const GalleryChildChip = ({
  node,
  selected,
  onClick,
  onDoubleClick,
  onContextMenu,
  parentPath,
}: {
  node: ChildTreeNode;
  selected?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  /** Right-click selects the chip before its menu opens, matching an asset tile. */
  onContextMenu?: () => void;
  /** Forwarded to the context menu's upload items — see `ChildNodeMenuItems`. */
  parentPath?: { id: string; namePath: Array<string> };
}) => {
  const selectedNode = toSelectedNode(node);
  const { setNodeRef, isOver } = useGalleryChildDropTarget(selectedNode);

  return (
    <div ref={setNodeRef} className="inline-flex">
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <NodeChip
            name={selectedNode.name}
            nodeType={selectedNode.type}
            selected={selected}
            isOver={isOver}
            dataCy={`node-chip-${selectedNode.id}`}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            // Runs alongside the trigger's own onContextMenu rather than replacing it: Radix's Slot
            // composes handlers for a prop both sides set, so the menu still opens.
            onContextMenu={onContextMenu}
            // Enter opens the child, Space selects it — the keyboard equivalents of double- and
            // single-click, which were otherwise unreachable: a <button>'s default handling fires
            // click for both keys, so Enter could only ever select. preventDefault stops Enter's
            // synthetic click from also running the select.
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onDoubleClick?.();
              }
            }}
          />
        </ContextMenuTrigger>
        <ContextMenuContent className="min-w-48">
          <ChildNodeMenuItems node={node} parentPath={parentPath} />
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
};
